import * as React from 'react'

import {
  DragLayerMonitorProps,
  NodeModel,
  Tree as ReactDndTree,
} from '@minoru/react-dnd-treeview'
import {
  ChevronDown,
  ChevronRight,
  FileText,
  GalleryHorizontalEnd,
  GripVertical,
  LayoutList,
  Pencil,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react'
import { Observer, observer } from 'mobx-react'
import Dropzone from 'react-dropzone'

import {
  IAttachment,
  IPermitTypeField,
  MediaViewType,
} from '~/client/graph/types.generated'
import AttachmentFileIcon from '~/client/src/desktop/views/Attachments/components/AttachmentFileIcon'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  FileKind,
  getFileKind,
} from '~/client/src/shared/components/FilesInput/fileKind'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/client/src/shared/components/shadcn-ui/select'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'

import InlineMediaConfiguratorStore, {
  ACCEPTED_MIME_TYPES,
  getFileExtension,
  UploadStatus,
} from './InlineMediaConfigurator.store'

interface IProps {
  field: IPermitTypeField
  onChange: (field: IPermitTypeField) => void
  fileUploadingStore: FileUploadingStore
  projectId: string
}

const MEDIA_TREE_ROOT_ID = 'inline-media-list-root'

@observer
export default class InlineMediaConfigurator extends React.Component<IProps> {
  private store: InlineMediaConfiguratorStore

  public constructor(props: IProps) {
    super(props)
    this.store = new InlineMediaConfiguratorStore(
      props.field,
      props.onChange,
      props.fileUploadingStore,
      props.projectId,
    )
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.field !== this.props.field) {
      this.store.setField(this.props.field)
    }
    if (prevProps.onChange !== this.props.onChange) {
      this.store.setOnChangeHandler(this.props.onChange)
    }
  }

  public render() {
    return (
      <div className="tw:flex tw:flex-col tw:gap-3 tw:p-4">
        {this.uploadZone}
        {this.uploadingItems}
        {this.store.hasItems ? this.fileList : this.emptyState}
        {this.store.totalCount > 1 && this.mediaViewSelector}
      </div>
    )
  }

  private get uploadZone(): JSX.Element {
    return (
      <Dropzone
        onDrop={this.store.handleFileDrop}
        accept={ACCEPTED_MIME_TYPES}
        multiple={true}
        className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:rounded-lg tw:border-2 tw:border-dashed tw:border-border tw:p-8 tw:cursor-pointer tw:transition-colors tw:hover:border-muted-foreground"
        activeClassName="tw:border-primary tw:bg-primary/5"
      >
        <Upload size={24} className="tw:text-muted-foreground" />
        <p className="tw:text-sm tw:text-foreground">
          Drag & Drop or{' '}
          <span className="tw:text-primary tw:cursor-pointer">Choose file</span>{' '}
          to upload
        </p>
        <p className="tw:text-xs tw:text-muted-foreground">
          Supports PDF, JPEG, JPG, PNG, MP4, WEBM
        </p>
      </Dropzone>
    )
  }

  private get emptyState(): JSX.Element {
    return (
      <p className="tw:text-sm tw:text-muted-foreground">
        No Inline Media Uploaded
      </p>
    )
  }

  private get uploadingItems(): JSX.Element {
    const { uploadingItems } = this.store
    if (!uploadingItems.length) return null

    return (
      <div className="tw:flex tw:flex-col tw:gap-1">
        {uploadingItems.map(item => {
          const ext = getFileExtension(item.fileName)
          return (
            <div
              key={item.id}
              className="tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-2"
            >
              {this.renderFileTypeIcon(item.fileName)}
              <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                <span className="tw:text-sm tw:truncate">{item.fileName}</span>
                {item.status === UploadStatus.Uploading && (
                  <div className="tw:flex tw:items-center tw:gap-2">
                    <span className="tw:text-xs tw:text-muted-foreground">
                      {ext} · Uploading...
                    </span>
                    <div className="tw:flex-1 tw:h-1 tw:bg-muted tw:rounded-full tw:overflow-hidden">
                      <div className="tw:h-full tw:bg-primary tw:rounded-full tw:animate-pulse tw:w-2/3" />
                    </div>
                  </div>
                )}
                {item.status === UploadStatus.Error && (
                  <span className="tw:text-xs tw:text-destructive">
                    {ext} · Error to upload
                  </span>
                )}
              </div>
              {item.status === UploadStatus.Error && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.store.retryUpload(item.id)}
                >
                  <RefreshCw size={14} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="tw:text-destructive"
                onClick={() => this.store.removeUploadingItem(item.id)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          )
        })}
      </div>
    )
  }

  private get fileList(): JSX.Element {
    const { mediaItems, totalCount, isUploadsExpanded } = this.store
    const isReorderEnabled = totalCount > 1

    const treeData: NodeModel<IAttachment>[] = mediaItems.map(
      (item, index) => ({
        id: item.url || String(index),
        parent: MEDIA_TREE_ROOT_ID,
        text: item.fileName,
        droppable: false,
        data: item,
      }),
    )

    return (
      <div className="tw:flex tw:flex-col tw:gap-1 tw:max-w-md">
        <Button
          variant="ghost"
          size="sm"
          className="tw:justify-start tw:px-0 tw:font-medium"
          onClick={this.store.toggleUploadsExpanded}
        >
          {isUploadsExpanded ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronRight size={14} />
          )}
          {totalCount} Uploaded Media:
        </Button>
        {isUploadsExpanded && (
          <div className="tw:rounded-lg tw:border tw:border-border">
            <ReactDndTree
              classes={{
                root: 'tw:list-none tw:p-0 tw:m-0 tw:divide-y tw:divide-border',
                draggingSource: 'tw:opacity-30',
                placeholder: 'tw:relative',
                dropTarget: 'tw:bg-primary/5',
              }}
              rootId={MEDIA_TREE_ROOT_ID}
              tree={treeData}
              sort={false}
              insertDroppableFirst={false}
              dropTargetOffset={10}
              canDrop={() => isReorderEnabled}
              render={this.renderTreeNode}
              placeholderRender={this.renderPlaceholder}
              dragPreviewRender={this.renderDragPreview}
              onDrop={this.handleTreeDrop}
            />
          </div>
        )}
      </div>
    )
  }

  private get mediaViewSelector(): JSX.Element {
    const currentView = this.props.field.mediaView || MediaViewType.Separated

    const items = [
      { value: MediaViewType.Separated, label: 'Separated' },
      { value: MediaViewType.GallerySlider, label: 'Gallery Slider' },
    ]

    return (
      <div className="tw:flex tw:flex-col tw:gap-1.5">
        <span className="tw:text-sm tw:font-medium tw:text-foreground">
          Media View
        </span>
        <Select
          value={currentView}
          onValueChange={value =>
            this.store.updateMediaView(value as MediaViewType)
          }
          items={items}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {items.map(item => (
              <SelectItem key={item.value} value={item.value}>
                <span className="tw:flex tw:items-center tw:gap-2">
                  {item.value === MediaViewType.Separated ? (
                    <LayoutList size={14} />
                  ) : (
                    <GalleryHorizontalEnd size={14} />
                  )}
                  {item.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  private renderTreeNode = (
    node: NodeModel<IAttachment>,
  ): JSX.Element | null => {
    const item = node.data
    if (!item) return null
    return <Observer>{() => this.renderFileRow(item)}</Observer>
  }

  private renderPlaceholder = (): JSX.Element => {
    return (
      <div className="tw:absolute tw:left-0 tw:right-0 tw:h-0.5 tw:bg-primary tw:rounded" />
    )
  }

  private renderDragPreview = ({
    item,
  }: DragLayerMonitorProps<IAttachment>): JSX.Element | null => {
    if (!item.data) return null
    const ext = getFileExtension(item.data.fileName)
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:bg-background tw:border tw:border-border tw:rounded-md tw:px-3 tw:py-2 tw:shadow-md tw:max-w-xs">
        <GripVertical size={14} className="tw:text-muted-foreground" />
        <span className="tw:text-sm tw:truncate">{item.data.fileName}</span>
        <span className="tw:text-xs tw:text-muted-foreground">{ext}</span>
      </div>
    )
  }

  private handleTreeDrop = (newTree: NodeModel<IAttachment>[]) => {
    const reordered = newTree.map(n => n.data).filter(Boolean)
    if (reordered.length === this.store.mediaItems.length) {
      this.store.reorderItems(reordered)
    }
  }

  private renderFileRow(item: IAttachment): JSX.Element {
    const ext = getFileExtension(item.fileName)
    const isEditing = this.store.editingItemUrl === item.url

    if (isEditing) {
      return (
        <div className="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2">
          <GripVertical
            size={16}
            className="tw:text-muted-foreground tw:shrink-0"
          />
          <Input
            className="tw:flex-1"
            value={this.store.editingName}
            onChange={e => this.store.updateEditingName(e.target.value)}
            onKeyDown={this.handleEditKeyDown}
            autoFocus
          />
          <Button
            variant="outline"
            size="sm"
            onClick={this.store.cancelEditing}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={this.store.saveEditing}>
            Save
          </Button>
        </div>
      )
    }

    return (
      <div className="tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-2">
        <GripVertical
          size={16}
          className="tw:text-muted-foreground tw:shrink-0 tw:cursor-grab"
        />
        {this.renderThumbnail(item)}
        <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
          <span className="tw:text-sm tw:truncate">{item.fileName}</span>
          <span className="tw:text-xs tw:text-muted-foreground">{ext}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => this.store.startEditing(item.url)}
        >
          <Pencil size={14} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="tw:text-destructive"
          onClick={() => this.store.deleteMediaItem(item.url)}
        >
          <Trash2 size={14} />
        </Button>
      </div>
    )
  }

  private renderThumbnail(item: IAttachment): JSX.Element {
    const kind = getFileKind(item.fileName)

    return (
      <div className="tw:relative tw:w-10 tw:h-10 tw:shrink-0">
        {this.renderThumbnailContent(item, kind)}
        {this.renderFileTypeBadge(item.fileName)}
      </div>
    )
  }

  private renderThumbnailContent(
    item: IAttachment,
    kind: FileKind,
  ): JSX.Element {
    if (kind === 'image') {
      return (
        <img
          src={item.url}
          alt={item.fileName}
          className="tw:w-full tw:h-full tw:rounded tw:object-cover"
        />
      )
    }

    if (kind === 'video') {
      return (
        <video
          src={item.url}
          className="tw:w-full tw:h-full tw:rounded tw:object-cover tw:bg-muted"
          muted
          preload="metadata"
        />
      )
    }

    return (
      <div className="tw:w-full tw:h-full tw:rounded tw:bg-muted tw:flex tw:items-center tw:justify-center">
        <FileText size={16} className="tw:text-muted-foreground" />
      </div>
    )
  }

  private renderFileTypeBadge(fileName: string): JSX.Element {
    return (
      <div className="tw:absolute tw:-bottom-0.5 tw:-left-0.5 tw:w-4 tw:h-4 tw:rounded-sm tw:border tw:border-border tw:bg-background tw:flex tw:items-center tw:justify-center">
        <AttachmentFileIcon fileName={fileName} className="" separator="." />
      </div>
    )
  }

  private renderFileTypeIcon(fileName: string): JSX.Element {
    return (
      <div className="tw:w-10 tw:h-10 tw:rounded tw:bg-muted tw:flex tw:items-center tw:justify-center tw:shrink-0">
        <AttachmentFileIcon fileName={fileName} className="" separator="." />
      </div>
    )
  }

  private handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      this.store.saveEditing()
    } else if (e.key === 'Escape') {
      this.store.cancelEditing()
    }
  }
}
